import { useState } from 'react'
import { Task, TaskStatus, TaskPriority } from '../types/task'
import './TaskItem.css'

interface TaskItemProps {
  task: Task
  onUpdate: (id: string, updates: Partial<Task>) => void
  onDelete: (id: string) => void
  onStatusChange: (id: string, status: TaskStatus) => void
  getUserName: (userId?: string) => string
}

export function TaskItem({ task, onDelete, onStatusChange, getUserName }: TaskItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [, setIsEditing] = useState(false)

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'urgent':
        return '#e74c3c'
      case 'high':
        return '#f39c12'
      case 'medium':
        return '#3498db'
      case 'low':
        return '#95a5a6'
      default:
        return '#95a5a6'
    }
  }

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return '#27ae60'
      case 'in-progress':
        return '#3498db'
      case 'pending':
        return '#f39c12'
      case 'cancelled':
        return '#95a5a6'
      default:
        return '#95a5a6'
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    })
  }

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed'

  return (
    <div className={`task-item ${task.status === 'completed' ? 'completed' : ''} ${isOverdue ? 'overdue' : ''}`}>
      <div className="task-item-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="task-item-main">
          <div className="task-item-checkbox">
            <input
              type="checkbox"
              checked={task.status === 'completed'}
              onChange={(e) => {
                e.stopPropagation()
                onStatusChange(
                  task.id,
                  e.target.checked ? 'completed' : 'pending'
                )
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="task-item-content">
            <div className="task-item-title">{task.title}</div>
            {task.dueDate && (
              <div className={`task-item-due-date ${isOverdue ? 'overdue' : ''}`}>
                Due: {formatDate(task.dueDate)}
              </div>
            )}
          </div>
        </div>
        <div className="task-item-badges">
          <span
            className="task-priority-badge"
            style={{ backgroundColor: getPriorityColor(task.priority) }}
          >
            {task.priority}
          </span>
          <span
            className="task-status-badge"
            style={{ backgroundColor: getStatusColor(task.status) }}
          >
            {task.status}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="task-item-details">
          {task.description && (
            <div className="task-item-description">{task.description}</div>
          )}

          <div className="task-item-meta">
            {task.assignedTo && (
              <div className="task-meta-item">
                <strong>Assigned to:</strong> {getUserName(task.assignedTo)}
              </div>
            )}
            {task.assignedBy && (
              <div className="task-meta-item">
                <strong>Assigned by:</strong> {getUserName(task.assignedBy)}
              </div>
            )}
            {task.tags && task.tags.length > 0 && (
              <div className="task-meta-item">
                <strong>Tags:</strong>{' '}
                {task.tags.map((tag, idx) => (
                  <span key={idx} className="task-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="task-item-actions">
            <button
              className="btn-task-action"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
            <button
              className="btn-task-action btn-task-delete"
              onClick={() => onDelete(task.id)}
            >
              Delete
            </button>
            {task.status !== 'completed' && task.status !== 'in-progress' && (
              <button
                className="btn-task-action"
                onClick={() => onStatusChange(task.id, 'in-progress')}
              >
                Start
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
































